import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { WheelDatePickerField } from '@/components/ui/wheel-date-picker';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { formatToLocalDate } from '@/lib/date';

interface FormWheelDatePickerFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label: string;
  placeholder?: string;
  description?: React.ReactNode;
  required?: boolean;
  className?: string;
  minYear?: number;
  maxYear?: number;
  onCommit?: () => void;
}

export const FormWheelDatePickerField = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  description,
  required,
  className,
  onCommit,
  ...props
}: FormWheelDatePickerFieldProps<TFieldValues>) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => {
      const dateValue = field.value as string | undefined;
      return (
        <FormItem className={className}>
          <FormLabel>
            {label} {required && <span className="text-primary">*</span>}
          </FormLabel>
          <FormControl>
            <WheelDatePickerField
              value={dateValue ? new Date(dateValue) : undefined}
              onChange={(date) => {
                field.onChange(formatToLocalDate(date), { shouldDirty: true });
              }}
              onCommit={onCommit}
              placeholder={placeholder}
              {...props}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      );
    }}
  />
);
FormWheelDatePickerField.displayName = 'FormWheelDatePickerField';
